import {
  AccountTree as DepartmentsIcon,
  Business as BranchesIcon,
  Category as ProductsIcon,
  CheckCircle,
  FilterList as FilterListIcon,
  Inventory2 as TotalIcon,
  People as CustomersIcon,
  Schedule,
  Support,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  CircularProgress,
  Container,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import ActiveUsersTable from "../components/reports/ActiveUsersTable";
import CategoryBarChart from "../components/reports/CategoryBarChart";
import MonthlyLineChart from "../components/reports/MonthlyLineChart";
import ReportsFilterDrawer from "../components/reports/ReportsFilterDrawer";
import ReportPdfExport, {
  ReportPdfHeader,
} from "../components/reports/ReportPdfExport";
import ReportsStatCard from "../components/reports/ReportsStatCard";
import ComplaintClassificationPieChart from "../components/reports/ComplaintClassificationPieChart";
import StatusPieChart from "../components/reports/StatusPieChart";
import { reportsAPI } from "../services/api";
import { loadAllReferenceData } from "../store/slices/referenceDataSlice";
import { fetchUserSearchOptions } from "../store/slices/ticketsSlice";
import {
  buildFilterParams,
  emptySearchFilters,
} from "../utils/buildSearchParams";
import { useRequirePermission } from "../hooks/useHasPermission";
import { PERMISSIONS } from "../utils/permissions";
import { buildFiltersSummary } from "../utils/reportFilters";

const ReportsPage = ({ onNavigate }) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const isRTL = i18n.language === "ar";
  const hasAccess = useRequirePermission(PERMISSIONS.VIEW_REPORTS, onNavigate);

  const referenceData = useSelector((state) => state.referenceData);
  const { userSearchOptions } = useSelector((state) => state.tickets);

  const [appliedFilters, setAppliedFilters] = useState(emptySearchFilters);
  const [draftFilters, setDraftFilters] = useState(emptySearchFilters);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pdfHeaderVisible, setPdfHeaderVisible] = useState(false);
  const pdfPage1Ref = useRef(null);
  const pdfPage2Ref = useRef(null);
  const pdfPage3Ref = useRef(null);

  const fetchDashboard = useCallback(async (filters) => {
    setLoading(true);
    setError(null);
    try {
      const params = buildFilterParams(filters);
      params.lang = i18n.language;
      const result = await reportsAPI.getDashboard(params);
      setData(result);
    } catch (err) {
      setError(err.message || t("reports.error"));
    } finally {
      setLoading(false);
    }
  }, [t, i18n.language]);

  useEffect(() => {
    dispatch(loadAllReferenceData());
    dispatch(fetchUserSearchOptions());
  }, [dispatch]);

  useEffect(() => {
    fetchDashboard(appliedFilters);
  }, [appliedFilters, fetchDashboard]);

  const handleOpenDrawer = () => {
    setDraftFilters(appliedFilters);
    setDrawerOpen(true);
  };

  const handleApply = () => {
    setAppliedFilters(draftFilters);
    setDrawerOpen(false);
  };

  const handleClear = () => {
    const cleared = emptySearchFilters();
    setDraftFilters(cleared);
    setAppliedFilters(cleared);
    setDrawerOpen(false);
  };

  const summary = data?.summary || {};
  const filtersSummary = useMemo(
    () =>
      buildFiltersSummary(
        appliedFilters,
        t,
        referenceData,
        userSearchOptions,
        i18n.language
      ),
    [appliedFilters, t, referenceData, userSearchOptions, i18n.language]
  );

  if (!hasAccess) {
    return null;
  }

  return (
    <Container
      dir={isRTL ? "rtl" : "ltr"}
      maxWidth="xl"
      sx={{ py: 3, textAlign: "start" }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
        }}
      >
        <Typography
          variant="h4"
          component="h1"
          fontWeight={700}
          sx={{ textAlign: "start" }}
        >
          {t("reports.title")}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <ReportPdfExport
            pageRefs={[pdfPage1Ref, pdfPage2Ref, pdfPage3Ref]}
            reportType="dashboard"
            onBeforeCapture={async () => setPdfHeaderVisible(true)}
            onAfterCapture={async () => setPdfHeaderVisible(false)}
          />
          <Tooltip title={t("reports.filter")}>
            <IconButton
              onClick={handleOpenDrawer}
              color="primary"
              aria-label={t("reports.filter")}
            >
              <FilterListIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3, textAlign: "start" }}>
          {error}
        </Alert>
      )}

      {loading && !data ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box>
          <Box ref={pdfPage1Ref}>
            <ReportPdfHeader
              visible={pdfHeaderVisible}
              title={t("reports.title")}
              filtersSummary={filtersSummary}
            />
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <ReportsStatCard
                  title={t("reports.stats.totalTickets")}
                  value={summary.total_tickets}
                  icon={<TotalIcon />}
                  color="primary.main"
                  isRTL={isRTL}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <ReportsStatCard
                  title={t("reports.stats.openTickets")}
                  value={summary.open_tickets}
                  icon={<Support color="warning" />}
                  color="warning.main"
                  isRTL={isRTL}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <ReportsStatCard
                  title={t("reports.stats.closedTickets")}
                  value={summary.closed_tickets}
                  icon={<CheckCircle color="success" />}
                  color="success.main"
                  isRTL={isRTL}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <ReportsStatCard
                  title={t("reports.stats.highPriority")}
                  value={summary.high_priority_tickets}
                  icon={<Schedule color="info" />}
                  color="info.main"
                  isRTL={isRTL}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <ReportsStatCard
                  title={t("reports.stats.customers")}
                  value={summary.customers_count}
                  icon={<CustomersIcon />}
                  color="secondary.main"
                  isRTL={isRTL}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <ReportsStatCard
                  title={t("reports.stats.branches")}
                  value={summary.branches_count}
                  icon={<BranchesIcon />}
                  color="primary.main"
                  isRTL={isRTL}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <ReportsStatCard
                  title={t("reports.stats.products")}
                  value={summary.products_count}
                  icon={<ProductsIcon />}
                  color="primary.main"
                  isRTL={isRTL}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <ReportsStatCard
                  title={t("reports.stats.departments")}
                  value={summary.departments_count}
                  icon={<DepartmentsIcon />}
                  color="primary.main"
                  isRTL={isRTL}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid size={{ xs: 12 }}>
                <MonthlyLineChart data={data?.monthly_tickets} isRTL={isRTL} />
              </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid size={{ xs: 12, md: 6 }}>
                <StatusPieChart data={data?.status_breakdown} isRTL={isRTL} />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <ComplaintClassificationPieChart
                  data={data?.complaint_classification_breakdown}
                  isRTL={isRTL}
                />
              </Grid>
            </Grid>
          </Box>

          <Box ref={pdfPage2Ref}>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid size={{ xs: 12 }}>
                <CategoryBarChart
                  title={t("reports.charts.branch")}
                  data={data?.by_branch}
                  isRTL={isRTL}
                  color="#1976d2"
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <CategoryBarChart
                  title={t("reports.charts.contactReason")}
                  data={data?.by_contact_reason}
                  isRTL={isRTL}
                  color="#2e7d32"
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <CategoryBarChart
                  title={t("reports.charts.product")}
                  data={data?.by_product}
                  isRTL={isRTL}
                  color="#d32f2f"
                />
              </Grid>
            </Grid>
          </Box>

          <Box ref={pdfPage3Ref}>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid size={{ xs: 12 }}>
                <CategoryBarChart
                  title={t("reports.charts.department")}
                  data={data?.by_department}
                  isRTL={isRTL}
                  color="#e65100"
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <CategoryBarChart
                  title={t("reports.charts.communicationMethod")}
                  data={data?.by_communication_method}
                  isRTL={isRTL}
                  color="#6a1b9a"
                />
              </Grid>
            </Grid>

            <ActiveUsersTable users={data?.active_users} isRTL={isRTL} />
          </Box>

          {loading && (
            <Box
              sx={{
                position: "fixed",
                bottom: 24,
                insetInlineEnd: 24,
                zIndex: 10,
              }}
            >
              <CircularProgress size={32} />
            </Box>
          )}
        </Box>
      )}

      <ReportsFilterDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        draftFilters={draftFilters}
        onDraftChange={setDraftFilters}
        onApply={handleApply}
        onClear={handleClear}
        isRTL={isRTL}
        referenceData={referenceData}
        userOptions={userSearchOptions}
      />
    </Container>
  );
};

export default ReportsPage;
